<script setup lang="ts">
import BaseDropdown from "@/components/Base/Dropdown/Dropdown.vue";
import BaseDropdownItem from "@/components/Base/Dropdown/Item/Item.vue";
import BaseIcon from "@/components/Base/Icon/Icon.vue";
import { LocaleEnum, useUpdateProfile } from "@/services/internalApiV1";

defineOptions({ name: "FooterLanguageDropdown" });

type Props = {
  dropdownId?: string;
};

withDefaults(defineProps<Props>(), {
  dropdownId: "footer-language",
});

const { t, locale } = useI18n();
const { mutateAsync: updateProfile } = useUpdateProfile();
const supportedLocales = Object.values(LocaleEnum) as LocaleEnum[];

const localeOptions = computed(() => {
  return supportedLocales.map((currentLocale) => ({
    value: currentLocale,
    label: t(`enums.languages.${currentLocale}`),
  }));
});

const selectedLocale = computed(() => {
  const currentLocale = locale.value as LocaleEnum;

  if (supportedLocales.includes(currentLocale)) {
    return currentLocale;
  }

  return LocaleEnum.DE;
});

const selectedLocaleLabel = computed(() => {
  return (
    localeOptions.value.find((o) => o.value === selectedLocale.value)?.label ??
    selectedLocale.value
  );
});

const switchLocale = async (nextLocale: LocaleEnum): Promise<void> => {
  if (nextLocale === selectedLocale.value) {
    return;
  }

  await updateProfile({ data: { profile: { locale: nextLocale } } });
  window.location.reload();
};
</script>

<template>
  <BaseDropdown
    :id="dropdownId"
    data-testid="footer-language-dropdown"
    :button-aria-label="t('footer.language')"
    kind="tertiary"
    tertiary-color="neutral"
    no-padding
  >
    <template #button>
      <span class="footer-language-dropdown__button">
        <BaseIcon icon-name="globe" aria-hidden="true" />
        {{ selectedLocaleLabel }}
        <BaseIcon icon-name="chevron-down" aria-hidden="true" />
      </span>
    </template>
    <BaseDropdownItem
      v-for="option in localeOptions"
      :key="option.value"
      :label="option.label"
      with-radio-check
      :checked="option.value === selectedLocale"
      @click="switchLocale(option.value)"
    />
  </BaseDropdown>
</template>

<style lang="scss" scoped>
.footer-language-dropdown {
  &__button {
    display: flex;
    gap: $spacer-1;
  }
}
</style>
